import { useState, useEffect } from 'react'
import { Image, Check } from 'components'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import * as S from './CardSelector.styled'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { getItemDisplayName } from 'utils/global/getItemDisplayName'
import { useTranslation } from 'react-i18next'

const getCardTestId = (type, card, index) => {
  if (type === 'products')
    return `product-card-${card.product_id ?? card.id ?? index}`
  if (type === 'themes') return `theme-card-${card.id ?? index}`
  return `card-${card.id ?? index}`
}

export const CardSelector = ({
  allowMultiple,
  cards,
  cardWidth,
  cardHeight,
  minHeight,
  cardPadding,
  cardIcon,
  cardTag,
  productIcon,
  iconHover,
  selectedState,
  cardAlign,
  cardJustify,
  prependURL,
  imageHeight,
  imageWidth,
  imageFit,
  imagePosition,
  matchProperty,
  type,
  selectedStateUpdate,
  ...props
}) => {
  const { t } = useTranslation()
  const handleError = useErrorHandler('CardSelector')
  const [selected, setSelected] = selectedState
  const [selectedItems, setSelectedItems] = useState(allowMultiple ? [] : null)
  const [iconVisible, setIconVisible] = useState('')

  useEffect(() => {
    setSelectedItems(selected)
  }, [selected, setSelected])

  const toggleSelect = async (card) => {
    try {
      if (allowMultiple) {
        if (selectedStateUpdate) {
          await selectedStateUpdate()
        }
        setSelected(card)
      } else {
        if (selected?.id === card.id) {
          setSelected(null)
        } else {
          setSelected(card)
        }
      }
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
  }

  const toggleHover = (cardId) => {
    if (!iconHover) return

    const isCurrentlyVisible = cardId === iconVisible

    if (isCurrentlyVisible) {
      setIconVisible('')
    } else {
      setIconVisible(cardId)
    }
  }

  const determineSelected = (card) => {
    if (allowMultiple) {
      return selectedItems.some(
        (item) => card.id === item[matchProperty || 'id'] && item.selected,
      )
    } else {
      return selectedItems?.id
        ? selectedItems?.id === card.id || selectedItems?.id === card.theme_id
        : selectedItems === card.id
    }
  }

  return (
    <Flex
      width="100%"
      justify={cardJustify}
      align={cardAlign}
      wrap="wrap"
      {...props}
    >
      {cards.map((card, cardIndex) => (
        <S.SelectableCard
          column
          justify="center"
          align="center"
          position="relative"
          key={`card-${cardIndex}`}
          id={`${card.title}-${cardIndex}`}
          data-testid={getCardTestId(type, card, cardIndex)}
          width={cardWidth}
          height={cardHeight}
          minHeight={minHeight}
          cardPadding={cardPadding}
          margin={cardIndex === cards.length - 1 ? '0 0 16px' : '0 16px 16px 0'}
          isSelected={determineSelected(card)}
          onClick={() => toggleSelect(card)}
          onMouseEnter={() => toggleHover(`${card.title}-${cardIndex}`)}
          onMouseLeave={() => toggleHover(`${card.title}-${cardIndex}`)}
        >
          {productIcon && (
            <Check
              position="absolute"
              top="12px"
              left="12px"
              checked={determineSelected(card)}
            />
          )}
          {cardIcon && (
            <S.CardIcon
              key={`${cardIcon.icon}-icon`}
              icon={cardIcon.icon}
              fill={card.theme_id || card.isPinned ? 'secondary' : 'gray3'}
              opacity={
                !iconHover ||
                iconVisible === `${card.title}-${cardIndex}` ||
                card.isPinned
                  ? 1
                  : 0
              }
              onClick={(e) => cardIcon.clickHandler(e, card)}
            />
          )}
          {cardTag && cardTag(card) && (
            <S.CardTag>{cardTag(card).label}</S.CardTag>
          )}
          {(card.image || card.thumbnail) && (
            <Flex align="center" height={imageHeight || '124px'}>
              <Image
                src={
                  prependURL
                    ? `${prependURL}${card.image || card.thumbnail}?w=250`
                    : card.image || card.thumbnail
                }
                width={imageWidth || '220px'}
                height={imageHeight || '124px'}
                objectFit={imageFit || 'contain'}
                objectPosition={imagePosition || 'center'}
              />
            </Flex>
          )}
          {(card?.name || card?.title) && (
            <Text
              size="14px"
              weight="bold"
              justify="center"
              align="center"
              whiteSpace="normal"
              width="100%"
            >
              {getItemDisplayName(card.name || card.title, type, t)}
            </Text>
          )}
          {card.description && type !== 'themes' && (
            <Text
              size="14px"
              color="gray2"
              justify="center"
              align="center"
              whiteSpace="wrap"
            >
              {card.description}
            </Text>
          )}
        </S.SelectableCard>
      ))}
    </Flex>
  )
}

export default CardSelector
